import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Box, Paper, IconButton, TextField, Typography, Fab } from '@mui/material'
import { Close, Refresh, Send, AutoAwesome, Mic, Stop } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useNavigateWithQueryParams, checkFile } from '../Utilities'
import { useKeycloak } from '../Keycloak'
import { useGetAiParameterEnabledQuery, useGetAccessMonitoringQuery } from '../../api/graphqlApi'
import { isMobile } from 'react-device-detect'
import * as ChatManager from './ChatManager'

const SPEECH_LANGS = { de: 'de-DE', en: 'en-US', nl: 'nl-NL' }

// Pre-launch: show mode switcher. Set to false for production.
const SHOW_MODE_SWITCHER = true

// ─── Usage Limits Panel (dev only) ────────────────────────────────────────

function UsageBar({ cost, limit }) {
    const pct = limit > 0 ? Math.min(cost / limit, 1) : 0
    const color = pct >= 0.8 ? '#e53935' : pct >= 0.5 ? '#f9a825' : '#43a047'
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Box sx={{ flex: 1, height: 5, bgcolor: '#e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ width: `${pct * 100}%`, height: '100%', bgcolor: color, borderRadius: 2, transition: 'width 0.3s' }} />
            </Box>
            <Box component="span" sx={{ fontSize: 9, color: '#666', minWidth: 70, textAlign: 'right', fontFamily: 'monospace' }}>
                {`$${cost.toFixed(3)} / $${limit.toFixed(2)}`}
            </Box>
        </Box>
    )
}

function UsageSection({ label, bucket }) {
    return (
        <Box sx={{ mb: 0.5 }}>
            <Box sx={{ fontSize: 9, fontWeight: 700, color: '#555', mb: 0.25 }}>{label}</Box>
            {['fourHour', 'daily', 'weekly'].map(key => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ fontSize: 8, color: '#999', width: 24, textAlign: 'right' }}>
                        {key === 'fourHour' ? '4h' : key === 'daily' ? 'day' : 'wk'}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <UsageBar cost={bucket[key].cost} limit={bucket[key].limit} />
                    </Box>
                </Box>
            ))}
        </Box>
    )
}

function UsageLimitsPanel({ details, open, onToggle }) {
    if (!details?.gatekeeper || !details?.expensive || !details?.availability) return null
    const { expensive, gatekeeper, globalDaily, availability } = details
    const statusColor = !availability.gatekeeper ? '#e53935' : !availability.expensive ? '#f9a825' : '#43a047'
    const statusText = !availability.gatekeeper ? 'Blocked' : !availability.expensive ? 'Degraded' : 'OK'

    return (
        <Box sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
            <Box
                onClick={onToggle}
                sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1, py: 0.25, cursor: 'pointer', userSelect: 'none',
                    '&:hover': { bgcolor: '#eeeeee' },
                }}
            >
                <Box sx={{ fontSize: 9, color: '#888' }}>
                    Usage Limits {open ? '▾' : '▸'}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor }} />
                    <Box sx={{ fontSize: 9, color: statusColor, fontWeight: 600 }}>{statusText}</Box>
                </Box>
            </Box>
            {open && (
                <Box sx={{ px: 1, pb: 0.75 }}>
                    <UsageSection label="Expensive (gpt-5.4)" bucket={expensive} />
                    <UsageSection label="Gatekeeper (gpt-5-mini)" bucket={gatekeeper} />
                    <Box sx={{ mb: 0.25 }}>
                        <Box sx={{ fontSize: 9, fontWeight: 700, color: '#555', mb: 0.25 }}>Global Daily</Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box component="span" sx={{ fontSize: 8, color: '#999', width: 24, textAlign: 'right' }}>day</Box>
                            <Box sx={{ flex: 1 }}>
                                <UsageBar cost={globalDaily.cost} limit={globalDaily.limit} />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

export default function ChatWidget() {

    const { t, i18n } = useTranslation()
    const navigate = useNavigateWithQueryParams()
    const { getUsername, isLoggedIn, isUsersPortal, hasRole } = useKeycloak()
    const conf = window.conf || {}

    // ── Capability detection ──────────────────────────────────────────────

    const username = getUsername()
    const isNetcoUser = username === 'netco-admin' || username === 'netco-user'

    // Dashboard: AI parameter 7 + not mobile
    const { data: dashboardData } = useGetAiParameterEnabledQuery(
        { aiParameterId: 7 }, { skip: !isNetcoUser }
    )
    const hasDashboard = isNetcoUser && dashboardData?.enabled && !isMobile

    // Access: role + data exists + authorized
    const { showAccess } = useGetAccessMonitoringQuery(undefined, {
        selectFromResult: ({ data, isSuccess }) => {
            if (!isSuccess) return { showAccess: false }
            const hasAnyData = (conf.CAMERAS ?? []).some(elem => {
                return (data?.byCameraId?.[elem?.id]?.length || 0) > 0
            })
            const ACCESS = conf.ROUTES?.ACCESS
            const isAuthorized = isLoggedIn() && isUsersPortal()
            const hasAccessPageRole = ACCESS && hasRole(ACCESS.role)
            return { showAccess: hasAnyData && isAuthorized && hasAccessPageRole }
        }
    })

    // Report: async check for project_report.pdf (runs once)
    const [reportExists, setReportExists] = useState(false)
    useEffect(() => {
        let cancelled = false
        const CAMERAS = conf.CAMERAS || []
        const BASEURL = conf.BASEURL || '/'
        if (!CAMERAS.length) return
        // Check first camera's report — if any exists, report capability is on
        Promise.all(
            CAMERAS.map(elem =>
                checkFile(`${BASEURL}cam${elem.id + 1}/reports/project_report.pdf`)
                    .then(r => r.exists && r.contentType?.includes('pdf'))
            )
        ).then(results => { if (!cancelled) setReportExists(results.some(Boolean)) })
        return () => { cancelled = true }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Build capabilities object from detected flags
    const capabilities = useMemo(() => ({
        pages: {
            home: true,
            live: true,
            timelapse: true,
            archive: true,
            mysite: true, // always in routes; events section is always available
            access: !!showAccess,
        },
        mysite: {
            dashboard: !!hasDashboard,
            report: reportExists,
            diary: conf.LAYOUT?.DIARY?.ENABLED ?? false,
            download: !isMobile,
            events: true,
        },
        features: {
            livestream: (conf.CAMERAS || []).some(c => !!c.video),
        },
    }), [hasDashboard, showAccess, reportExists, conf.LAYOUT?.DIARY?.ENABLED]) // eslint-disable-line react-hooks/exhaustive-deps

    // register navigate function so ChatManager can navigate from anywhere
    useEffect(() => {
        ChatManager.setNavigate(navigate)
        return () => ChatManager.setNavigate(null)
    }, [navigate])

    const [isOpen, setIsOpen] = useState(false)
    const [messages, setMessages] = useState([{ role: 'assistant', text: t('chatbot.greeting') }])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [chatMode, setChatMode] = useState(ChatManager.getMode())
    const [lastModel, setLastModel] = useState(null) // model that answered last message
    const [isListening, setIsListening] = useState(false)
    const [speechSupported, setSpeechSupported] = useState(false)
    const [usageDetails, setUsageDetails] = useState(null)
    const [usageOpen, setUsageOpen] = useState(false)

    const recognitionRef = useRef(null)
    const messagesEndRef = useRef(null)
    const initDone = useRef(false)

    // refresh usage data for display
    const updateUsageDisplay = useCallback(() => {
        try {
            const d = ChatManager.getUsageDetail()
            if (d) setUsageDetails(d)
        } catch (e) { /* not initialized yet */ }
    }, [])

    // init chatbot system once on first open (mode detection, capabilities, RAG preload)
    useEffect(() => {
        if (isOpen && !initDone.current) {
            initDone.current = true
            ChatManager.init({ username, capabilities }).then(async () => {
                setChatMode(ChatManager.getMode())
                // In backend mode, fetch usage from server before first message
                if (ChatManager.getMode() === 'backend') {
                    await ChatManager.fetchBackendUsage()
                }
                updateUsageDisplay()
            })
        }
    }, [isOpen, username, capabilities, updateUsageDisplay])

    // auto-scroll to bottom when messages change
    useEffect(() => {
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }, [messages, loading])

    // speech recognition setup
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        if (!SpeechRecognition) return
        setSpeechSupported(true)
        const recognition = new SpeechRecognition()
        recognition.lang = SPEECH_LANGS[i18n.language] || 'de-DE'
        recognition.interimResults = true
        recognition.continuous = false
        recognition.onresult = (event) => {
            let transcript = ''
            for (let i = 0; i < event.results.length; i++) transcript += event.results[i][0].transcript
            setInput(transcript)
        }
        recognition.onend = () => setIsListening(false)
        recognition.onerror = (event) => {
            setIsListening(false)
            if (event.error === 'not-allowed') alert(t('chatbot.mic_denied'))
        }
        recognitionRef.current = recognition
        return () => recognition.abort()
    }, [i18n.language]) // eslint-disable-line react-hooks/exhaustive-deps

    // build dashboard state snapshot for AI context (if dashboard is active)
    const buildDashboardState = useCallback(() => {
        const actions = ChatManager.getDashboardActions()
        if (!actions) return undefined
        return {
            active_tab: actions.getActiveTab(),
            date_range: actions.getDateRange(),
            detection_types_enabled: actions.getEnabledDetectionTypes(),
            detection_types_available: actions.getDetectionTypes(),
            weather_streams: actions.getWeatherStreams(),
            data_loaded: actions.getDataLoaded(),
            viewer_open: actions.getViewerDate() !== null,
            viewer_date: actions.getViewerDate()
        }
    }, [])

    // wait for dashboardActions to become available after navigation (polls every 200ms, max 15s)
    const waitForDashboardActions = useCallback(() => {
        return new Promise(resolve => {
            let elapsed = 0
            const id = setInterval(() => {
                const actions = ChatManager.getDashboardActions()
                if (actions) { clearInterval(id); resolve(actions) }
                elapsed += 200
                if (elapsed >= 15000) { clearInterval(id); resolve(null) }
            }, 200)
        })
    }, [])

    // execute dashboard actions, returns description strings
    const executeDashboardActions = useCallback((actions, dashboardActions) => {
        const descriptions = []
        const typeFilterAct = actions.find(a => a.action === 'show_only_detections')
        const allTypesAct = actions.find(a => a.action === 'show_all_detections')
        const setDatesAct = actions.find(a => a.action === 'set_dates')
        const typesOverride = setDatesAct && typeFilterAct ? typeFilterAct.types
            : setDatesAct && allTypesAct ? null : undefined

        for (const act of actions) {
            switch (act.action) {
                case 'show_only_detections':
                    if (!setDatesAct) dashboardActions.showOnlyDetections(act.types)
                    descriptions.push(t('chatbot.act_only') + ' ' + act.types.join(', '))
                    break
                case 'show_all_detections':
                    if (!setDatesAct) dashboardActions.showAllDetections()
                    descriptions.push(t('chatbot.act_all_types'))
                    break
                case 'set_dates':
                    dashboardActions.setDates(act.start, act.end, typesOverride)
                    descriptions.push(t('chatbot.act_dates'))
                    break
                case 'switch_tab': {
                    // Map German tab names to internal values (defense against unmapped actions)
                    const tabMap = { erkennungen: 'detections', wetter: 'weather' }
                    const tab = tabMap[act.tab] || act.tab
                    dashboardActions.switchTab(tab)
                    descriptions.push(t('chatbot.act_tab') + ' ' + tab)
                    break
                }
                case 'show_weather_streams':
                    dashboardActions.showWeatherStreams(act.streams)
                    descriptions.push(t('chatbot.act_streams') + ' ' + act.streams.join(', '))
                    break
                case 'reset_view':
                    dashboardActions.resetView()
                    descriptions.push(t('chatbot.act_reset'))
                    break
                case 'load_data':
                    descriptions.push(t('chatbot.act_loading'))
                    break
                case 'open_viewer':
                    dashboardActions.openViewer(act.date, act.timestamp || null, act.types || null)
                    descriptions.push(t('chatbot.act_viewer') + (act.date ? ' ' + t('chatbot.act_for') + ' ' + act.date : ''))
                    break
                default:
                    break
            }
        }
        return descriptions
    }, [t])

    // send message to AI
    const handleSend = useCallback(async () => {
        const text = input.trim()
        if (!text || loading) return

        setInput('')
        setMessages(prev => [...prev, { role: 'user', text }])
        setLoading(true)

        try {
            const json = await ChatManager.sendMessage(text, buildDashboardState(), {
                language: i18n.language,
            })
            const newMsgs = [{ role: 'assistant', text: json.reply || t('chatbot.no_response') }]

            // Update model indicator from debug info
            if (json.debug) {
                const model = json.debug.expensive?.model || json.debug.gatekeeper?.model || json.debug.model_used || null
                setLastModel(model)
            }

            if (json.actions?.length) {
                let dashboardActions = ChatManager.getDashboardActions()

                // if dashboard is not active, navigate there and wait for it
                if (!dashboardActions) {
                    setMessages(prev => [...prev, ...newMsgs, { role: 'system', text: t('chatbot.act_navigating') }])
                    newMsgs.length = 0 // prevent re-adding reply in final setMessages
                    await ChatManager.navigateToDashboard()
                    dashboardActions = await waitForDashboardActions()
                }

                if (dashboardActions) {
                    const descriptions = executeDashboardActions(json.actions, dashboardActions)
                    if (descriptions.length) newMsgs.push({ role: 'system', text: t('chatbot.updated') + ' ' + descriptions.join('; ') })
                }
            }

            setMessages(prev => [...prev, ...newMsgs])
            setChatMode(ChatManager.getMode())
            updateUsageDisplay()
        } catch (e) {
            setMessages(prev => [...prev, { role: 'system', text: t('chatbot.error') }])
        } finally {
            setLoading(false)
        }
    }, [input, loading, buildDashboardState, waitForDashboardActions, executeDashboardActions, t, i18n.language]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }

    const toggleListening = useCallback(() => {
        const recognition = recognitionRef.current
        if (!recognition) return
        if (isListening) { recognition.abort(); setIsListening(false) }
        else {
            setInput('')
            try { recognition.start(); setIsListening(true) } catch (e) { setIsListening(false) }
        }
    }, [isListening])

    // cycle mode: backend → mock → backend
    const cycleMode = useCallback(() => {
        const modes = ['backend', 'mock']
        const idx = modes.indexOf(chatMode)
        const next = modes[(idx + 1) % modes.length]
        ChatManager.setMode(next)
        setChatMode(next)
        setLastModel(null)
    }, [chatMode])

    const resetChat = () => {
        ChatManager.resetConversation()
        setMessages([{ role: 'assistant', text: t('chatbot.new_greeting') }])
        setLastModel(null)
    }

    // closed state: floating bubble
    if (!isOpen) {
        return (
            <Fab
                onClick={() => setIsOpen(true)}
                title={t('chatbot.title')}
                sx={{
                    position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
                    bgcolor: '#003366', color: 'white', width: 56, height: 56,
                    '&:hover': { bgcolor: '#004080' },
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                }}
            >
                <AutoAwesome />
            </Fab>
        )
    }

    // open state: chat panel
    const modeBgColors = { backend: '#7c3aed', mock: '#f5f5f5' }
    const modeLabels = { backend: 'Backend', mock: t('chatbot.mode_mock') }

    // Model indicator: small label showing which model answered
    const modelLabel = lastModel ? (
        <Box component="span" sx={{ ml: 0.5, opacity: 0.8, fontSize: 10 }}>
            ({lastModel})
        </Box>
    ) : null

    return (
        <Paper
            elevation={8}
            sx={{
                position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
                width: 380, maxHeight: 520, minHeight: 320, display: 'flex', flexDirection: 'column',
                borderRadius: 3, overflow: 'hidden',
                animation: 'chatSlideUp 0.25s ease-out',
            }}
        >

            {/* header */}
            <Box sx={{
                bgcolor: '#003366', color: 'white', px: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                minHeight: 48,
            }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 14 }}>
                    {t('chatbot.title')}
                </Typography>
                <Box>
                    <IconButton size="small" onClick={resetChat} title={t('chatbot.new')} sx={{ color: 'white' }}>
                        <Refresh fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setIsOpen(false)} title={t('chatbot.close')} sx={{ color: 'white' }}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* mode badge — clickable to cycle modes (pre-launch only) */}
            <Box
                onClick={SHOW_MODE_SWITCHER ? cycleMode : undefined}
                sx={{
                    textAlign: 'center', py: 0.5, fontSize: 11, fontWeight: 600,
                    color: chatMode === 'mock' ? '#999' : 'white',
                    bgcolor: modeBgColors[chatMode],
                    cursor: SHOW_MODE_SWITCHER ? 'pointer' : 'default',
                    userSelect: 'none',
                    '&:hover': SHOW_MODE_SWITCHER ? { filter: 'brightness(1.1)' } : {},
                }}
            >
                {modeLabels[chatMode]}
                {modelLabel}
            </Box>

            {/* usage limits panel + diagnostic export (dev/testing only) */}
            {SHOW_MODE_SWITCHER && usageDetails && (
                <UsageLimitsPanel details={usageDetails} open={usageOpen} onToggle={() => setUsageOpen(o => !o)} />
            )}
            {SHOW_MODE_SWITCHER && (
                <Box sx={{ textAlign: 'right', px: 1, py: 0.25, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
                    <Box
                        component="span"
                        onClick={() => {
                            const text = ChatManager.getDiagnosticLog()
                            const blob = new Blob([text], { type: 'text/plain' })
                            const url = URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = `chatbot_diag_${new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')}.log`
                            a.click()
                            URL.revokeObjectURL(url)
                        }}
                        sx={{ fontSize: 9, color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Export diagnostic log ({ChatManager.getDiagnosticCount()} entries)
                    </Box>
                </Box>
            )}

            {/* messages */}
            <Box sx={{
                flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1,
                minHeight: 0, maxHeight: 340,
                bgcolor: '#fafafa',
            }}>
                {messages.map((msg, i) => (
                    <Box key={i} sx={{
                        alignSelf: msg.role === 'user' ? 'flex-end' : msg.role === 'system' ? 'center' : 'flex-start',
                        bgcolor: msg.role === 'user' ? '#003366' : msg.role === 'system' ? 'transparent' : '#f0f2f5',
                        color: msg.role === 'user' ? 'white' : msg.role === 'system' ? '#999' : '#2c3e50',
                        fontStyle: msg.role === 'system' ? 'italic' : 'normal',
                        fontSize: msg.role === 'system' ? 12 : 13,
                        px: msg.role === 'system' ? 1 : 1.5,
                        py: msg.role === 'system' ? 0.5 : 1,
                        borderRadius: 2,
                        maxWidth: msg.role === 'system' ? '100%' : '85%',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5,
                    }}>
                        {msg.text}
                    </Box>
                ))}

                {/* typing indicator */}
                {loading && (
                    <Box sx={{ alignSelf: 'flex-start', display: 'flex', gap: 0.5, p: 1 }}>
                        <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0s' }} />
                        <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0.15s' }} />
                        <Box component="span" className="chat-dot" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#bbb', animationDelay: '0.3s' }} />
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* input row */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, p: 1, borderTop: '1px solid #e0e0e0', bgcolor: 'white',
            }}>
                {speechSupported && (
                    <IconButton
                        size="small"
                        onClick={toggleListening}
                        disabled={loading}
                        sx={{
                            color: isListening ? 'white' : '#666',
                            bgcolor: isListening ? '#e53935' : 'transparent',
                            animation: isListening ? 'micPulse 1.5s ease-in-out infinite' : 'none',
                            '&:hover': { bgcolor: isListening ? '#c62828' : 'rgba(0,0,0,0.04)' },
                        }}
                        title={isListening ? t('chatbot.speech_stop') : t('chatbot.speech')}
                    >
                        {isListening ? <Stop fontSize="small" /> : <Mic fontSize="small" />}
                    </IconButton>
                )}
                <TextField
                    size="small"
                    fullWidth
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isListening ? t('chatbot.listening') : t('chatbot.placeholder')}
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 5, fontSize: 13 } }}
                />
                <IconButton
                    size="small"
                    onClick={handleSend}
                    disabled={loading || !input.trim()}
                    sx={{
                        bgcolor: '#003366', color: 'white',
                        '&:hover': { bgcolor: '#004080' },
                        '&:disabled': { bgcolor: '#ccc', color: 'white' },
                    }}
                >
                    <Send fontSize="small" />
                </IconButton>
            </Box>
        </Paper>
    )
}
